import { EntityLink as Link } from '../../navigation/navigation'
import { useListState } from '../../navigation/useListState'
import React, { useState, useEffect } from 'react'
import { useEntityNavigate as useNavigate } from '../../navigation/navigation'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { usePageHeader } from '../../contexts/PageHeaderContext'
import EmployeeAvatar from '../../components/EmployeeAvatar'
import WorkloadIndicator from '../../components/WorkloadIndicator'
import Icon from '../../components/Icon'
import { Button, Input, Select, TagBadge } from '../../components/ui'
import { translateApiError } from '../../utils/errorMessages'
import './Company.css'

function Team() {
  const { filters, setFilters, viewMode, setViewMode, sortBy, setSortBy } = useListState({
    filters: {
      department: '',
      search: '',
      workload: ''
    },
    viewMode: 'table',
    sortBy: 'name',
  })
  const { user } = useAuth()
  const [employees, setEmployees] = useState([])
  const [departments, setDepartments] = useState([])
  const [loading, setLoading] = useState(true)

  const navigate = useNavigate()
  const canManageEmployees = user?.role === 'admin' || user?.role === 'management'
  const canViewReports = user?.permissions?.['reports.view'] === true

  usePageHeader({
    renderActions: () => canManageEmployees ? (
      <Button type="button" variant="primary" onClick={() => navigate('/company/team/new')}>
        + Создать сотрудника
      </Button>
    ) : null
  }, [canManageEmployees, navigate])

  useEffect(() => {
    fetchDepartments()
    // Проверяем параметры URL при загрузке
    const params = new URLSearchParams(window.location.search)
    const departmentParam = params.get('department')
    if (departmentParam) {
      setFilters(prev => ({ ...prev, department: departmentParam }))
    }
  }, [])

  useEffect(() => {
    fetchEmployees()
  }, [filters])

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/company/departments')
      if (response.data && response.data.departments) {
        setDepartments(response.data.departments)
      }
    } catch (error) {
      console.error('Error fetching departments:', error)
    }
  }

  const fetchEmployees = async () => {
    try {
      setLoading(true)
      const params = {}
      if (filters.department) params.department = filters.department
      if (filters.search) params.search = filters.search

      const response = await api.get('/company/employees', { params })
      if (response.data && response.data.employees) {
        setEmployees(response.data.employees)
      } else {
        setEmployees([])
      }
    } catch (error) {
      console.error('Error fetching employees:', error)
      alert('Ошибка загрузки сотрудников: ' + translateApiError(error.response?.data?.error || error.message))
      setEmployees([])
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleRowClick = (employee) => {
    const employeeName = employee?.id || employee?.full_name || employee?.name || employee
    if (employeeName && typeof employeeName === 'string') {
      navigate(`/company/team/${encodeURIComponent(employeeName)}`)
    }
  }

  // Сортируем сотрудников
  const sortedEmployees = [...employees].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return (a.full_name || '').localeCompare(b.full_name || '')
      case 'department':
        return (a.department || '').localeCompare(b.department || '')
      case 'workload':
        const aWorkload = a.workload_percentage || 0
        const bWorkload = b.workload_percentage || 0
        return bWorkload - aWorkload
      default:
        return 0
    }
  })

  // Фильтруем по загрузке
  const filteredEmployees = sortedEmployees.filter(emp => {
    if (!filters.workload) return true
    const workload = emp.workload_percentage || 0
    switch (filters.workload) {
      case 'low':
        return workload < 50
      case 'normal':
        return workload >= 50 && workload < 80
      case 'high':
        return workload >= 80
      default:
        return true
    }
  })

  // Заглушка для статуса (онлайн/офлайн)
  const getStatus = (employee) => {
    // Заглушка: случайный статус для демонстрации
    const statuses = ['online', 'offline', 'away']
    const index = (employee.id || '').length % 3
    return statuses[index]
  }

  // Количество активных проектов (как в карточке сотрудника: уникальные стадии из stage_summary)
  const getProjectsCount = (employee) => {
    return employee.active_projects_count ?? employee.projects_count ?? 0
  }

  return (
    <div className="company-page">
      <div className="page-header">
        <div className="view-controls">
          <button
            className={`view-toggle ${viewMode === 'cards' ? 'active' : ''}`}
            onClick={() => setViewMode('cards')}
            title="Карточки"
            aria-label="Режим карточек"
            aria-pressed={viewMode === 'cards'}
          >
            <Icon name="grid" size={18} />
          </button>
          <button
            className={`view-toggle ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => setViewMode('table')}
            title="Таблица"
            aria-label="Режим таблицы"
            aria-pressed={viewMode === 'table'}
          >
            <Icon name="table" size={18} />
          </button>
        </div>
      </div>

      <div className="filters-section team-filters-row">
        <div className="filters">
          <div className="filter-group">
            <label>Подразделение:</label>
            <Select
              value={filters.department}
              onChange={(e) => handleFilterChange('department', e.target.value)}
              aria-label="Подразделение"
            >
              <option value="">Все подразделения</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.name}>
                  {dept.name} ({dept.employees_count})
                </option>
              ))}
            </Select>
          </div>

          <div className="filter-group">
            <label>Поиск:</label>
            <Input
              type="text"
              placeholder="Поиск по ФИО..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              aria-label="Поиск по ФИО"
            />
          </div>

          <div className="filter-group">
            <label>Сортировка:</label>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              aria-label="Сортировка"
            >
              <option value="name">По имени</option>
              <option value="department">По подразделению</option>
            </Select>
          </div>
        </div>
        {canViewReports && (
          <div className="team-filters-actions">
            <Button type="button" variant="secondary" onClick={() => navigate('/company/reports')}>
              <Icon name="fileCheck" size={18} /> Отчетность
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="loading">Загрузка...</div>
      ) : (
        <>
          {filteredEmployees.length === 0 ? (
            <p className="no-data">Сотрудники не найдены</p>
          ) : viewMode === 'cards' ? (
            <div className="employees-grid">
              {filteredEmployees.map(employee => (
                <div
                  key={employee.id}
                  className="employee-card-preview ui-card ui-card-hover"
                  onClick={() => handleRowClick(employee)}
                >
                  <div className="employee-card-header-preview">
                    <EmployeeAvatar
                      employee={employee}
                      size="large"
                      showStatus={true}
                      status={getStatus(employee)}
                    />
                    <div className="employee-card-info">
                      <Link className="employee-card-name" to={`/company/team/${encodeURIComponent(employee.id)}`}>{employee.full_name || '-'}</Link>
                      {employee.departments && employee.departments.length > 0 ? (
                        <div className="department-tags-wrap">
                          {employee.departments.map((dept, idx) => (
                            <TagBadge key={idx}>{dept}</TagBadge>
                          ))}
                        </div>
                      ) : (
                        <p className="employee-card-department">{employee.department || '-'}</p>
                      )}
                    </div>
                  </div>
                  <div className="employee-card-stats">
                    <div className="employee-stat-item">
                      <span className="stat-label">Проектов:</span>
                      <span className="stat-value">{getProjectsCount(employee)}</span>
                    </div>
                    <div className="employee-stat-item">
                      <span className="stat-label">Загрузка:</span>
                      <WorkloadIndicator
                        percentage={employee.workload_percentage}
                        showLabel={false}
                        size="small"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="table-section">
              <div className="table-responsive">
                <table className="ui-table data-table">
                  <thead>
                    <tr>
                      <th>ФИО</th>
                      <th>Подразделения</th>
                      <th className="text-right">Проектов</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map(employee => (
                      <tr
                        key={employee.id}
                        onClick={() => handleRowClick(employee)}
                        className="clickable-row"
                      >
                        <td>
                          <div className="table-cell-with-avatar">
                            <EmployeeAvatar
                              employee={employee}
                              size="small"
                              showStatus={true}
                              status={getStatus(employee)}
                            />
                            <Link className="list-primary-name" to={`/company/team/${encodeURIComponent(employee.id)}`}>{employee.full_name || '-'}</Link>
                          </div>
                        </td>
                        <td>
                          {employee.departments && employee.departments.length > 0 ? (
                            <div className="department-tags-wrap">
                              {employee.departments.map((dept, idx) => (
                                <TagBadge key={idx}>{dept}</TagBadge>
                              ))}
                            </div>
                          ) : (
                            employee.department || '-'
                          )}
                        </td>
                        <td className="text-right mono">{getProjectsCount(employee)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

    </div>
  )
}

export default Team
